import { NewspaperIcon } from "@heroicons/react/20/solid";
import CardContainer from "@/common/cards/CardContainer";
import ListView from "@/common/custom-lists/ListView";
import TableViewList from "@/common/custom-selectors/TableViewList";
import PayPeriodModals from "@/integrations/PayPeriodModals";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Actions as PAYPERIOD_ACTIONS } from "../../store/actions/payPeriodActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";
import LoadingContent from "../common/LoadingContent";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import ImportPayrollHistory from "./ImportPayrollHistory";
import ImportPayrollHistoryModal from "./ImportPayrollHistoryModal";
import PayrollRow from "./PayrollRow";
import BankTransferModal from "./bank-transfer-modal/BankTransferModal";
import { useQuery } from "@/components/common/helpers/ReactHooks";
import HMRCErrorsModal from "@/components/integrations/HMRC/HMRCErrorsModal";

const viewOptions = ["Normal", "Detailed", "Card"].map((value) => ({ label: value, value }));

const PayrollHistory = ({ getPayPeriods, org, years, payrolls, paySchedules, userPermissions, jurisdiction }) => {
	const storedView = localStorage.getItem("payroll-history-view");
	const [paySchedule, setPaySchedule] = useQuery("paySchedule");
	const [year, setYear] = useQuery("year");
	const [view, setView] = useState(viewOptions.find(({ value }) => value == storedView));

	const currentYear = new Date().getFullYear();

	const filteredPayrolls = useMemo(() => {
		const filterByValue = (period) =>
			(paySchedule ? period.paySchedule == paySchedule : true) && (year ? period.year == year : true);

		return paySchedule || year ? payrolls?.filter(filterByValue) : payrolls;
	}, [paySchedule, year, payrolls]);

	useEffect(() => {
		getPayPeriods();
	}, [org]);

	useEffect(() => {
		if (storedView) setView(viewOptions.find(({ value }) => value == storedView));
	}, [storedView]);

	useEffect(() => {
		const latestYear = years?.find(({ value }) => value == currentYear)?.value || null;
		if (!year) setYear(latestYear);
		if (!year && !latestYear) setYear(years[0]?.value);
	}, [JSON.stringify(years || [])]);

	const columns = useMemo(() => {
		const columns = [];

		switch (view?.value) {
			case "Card":
				columns.push(...["Periods"]);
				break;
			case "Detailed":
				columns.push(...[{ hidden: true }, "Period", "Net Salary", "Taxes", "Actions", { hidden: true }]);
				break;
			default:
			case "Normal":
				columns.push(...[{ hidden: true }, "Period", "Overview", "Actions", { hidden: true }]);
				break;
		}
		return columns;
	}, [view?.value]);

	if (userPermissions == "None") return <InvalidPermissions />;
	if (!payrolls) return <LoadingContent />;

	return (
		<>
			<PayPeriodModals />
			<BankTransferModal />
			<HMRCErrorsModal />
			<ImportPayrollHistoryModal />
			<div>
				<div className="content bg-gray-lighter">
					<div className="row">
						<div className="col-sm-12">
							<div className="pull-left">
								<h1 className="page-heading">Payroll History</h1>
							</div>
						</div>
					</div>
				</div>
				<div className="content bg-white mb-32">
					<div className="block-header pr-0 pl-0">
						<div className="pull-left">
							<Link
								to="/organisation/payroll-history/payslips"
								className="btn btn-default"
							>
								<NewspaperIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Payslips</div>
							</Link>
						</div>
						{years?.length > 1 && (
							<div
								className="pull-right"
								style={{ width: 150, marginLeft: 20 }}
							>
								<div className="form-material">
									<Select
										isClearable
										styles={{
											menu: (provided) => ({ ...provided, zIndex: 10 }),
										}}
										options={years}
										value={years?.find((y) => y.value == year)}
										onChange={(selected) => {
											setYear(selected?.value);
										}}
									/>
									<label>Year</label>
								</div>
							</div>
						)}
						{paySchedules?.length > 1 && (
							<div
								className="pull-right"
								style={{ width: 150 }}
							>
								<div className="form-material">
									<Select
										isClearable
										options={paySchedules}
										styles={{
											menu: (provided) => ({ ...provided, zIndex: 10 }),
										}}
										value={paySchedules?.find((s) => s.value == paySchedule)}
										onChange={(selected) => {
											setPaySchedule(selected?.value);
										}}
									/>
									<label>Pay Schedule</label>
								</div>
							</div>
						)}
					</div>
					<ListView
						{...{
							title: "Payroll History",
							items:
								view?.value !== "Card"
									? filteredPayrolls?.map((period) => (
											<PayrollRow
												key={period.id}
												period={period}
												view={view?.value}
											/>
										))
									: null,
							columns: columns,
							viewList: (
								<div className="pull-right">
									<TableViewList
										options={viewOptions}
										value={view}
										onChange={(option) => {
											setView(option);
											localStorage.setItem("payroll-history-view", option.value);
										}}
									/>
								</div>
							),
						}}
					/>
					{view?.value === "Card" ? (
						<CardContainer>
							{filteredPayrolls?.map((period) => (
								<PayrollRow
									key={period.id}
									period={period}
									view={view?.value}
								/>
							))}
						</CardContainer>
					) : null}
					{userPermissions != "Read" && jurisdiction === "MT" && (
						<div className="block-header pull-left">
							<ImportPayrollHistory />
						</div>
					)}
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const { periods } = state?.payPeriods || {};

	const sortedPeriods = periods?.map((payroll, i) => ({
		...(payroll || {}),
		year: Number(payroll?.year),
		periodSort: Number(payroll?.period) / 12 / (Number(payroll?.payPeriods ?? 52) / 12),
	}));

	const groupedPeriods = _.groupBy(sortedPeriods, "paySchedule");

	const activePeriods = Object.entries(groupedPeriods || {}).reduce((acc, [key, value]) => {
		const allPayrolls = _.orderBy(value, ["year", "periodSort"], ["asc", "asc"]);

		allPayrolls.forEach((payroll, index) => {
			const complete = !["false", "no", false, undefined].includes(payroll?.complete);
			const active = allPayrolls.length === index + 1 && !complete;
			acc.push({ ...payroll, active });
		});
		return acc;
	}, []);

	const payrolls = _.orderBy(activePeriods, ["year", "periodSort"], ["desc", "desc"]);

	const userPermissions = state?.permissions?.my?.permissions?.payroll;

	const years =
		state?.payPeriods?.periods
			?.map((payroll) => Number(payroll?.year))
			?.filter((predicate, index, array) => {
				return array.indexOf(predicate) === index;
			})
			?.map((year) => ({
				label: year,
				value: year,
			})) || [];

	const paySchedules = state.company?.settings?.paySchedules?.map((schedule) => ({
		label: schedule.name,
		value: schedule.id,
	}));

	return {
		payrolls,
		paySchedules,
		years: years && _.orderBy(years, ["value"], ["desc"]),
		userPermissions,
		org: state?.company?.settings?.org,
		jurisdiction: state.company?.settings?.jurisdiction,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getPayrollHistory: () => dispatch(PAYROLL_ACTIONS.getPayrollHistory()),
	getPayPeriods: () => dispatch(PAYPERIOD_ACTIONS.getPayPeriods()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PayrollHistory);
